/**
 * 전역 인증 상태 관리 클래스
 * 토큰 갱신 상태 및 동시성 제어
 */
import TokenManager from './tokenManager';
import RetryManager, { retryMonitor } from './retryManager';
import JWTValidator from './jwtValidator';

export interface AuthState {
    isRefreshing: boolean;
    refreshPromise: Promise<string> | null;
    lastRefreshTime: number;
    refreshAttempts: number;
}

export class AuthStateManager {
    private static instance: AuthStateManager;
    private state: AuthState = {
        isRefreshing: false,
        refreshPromise: null,
        lastRefreshTime: 0,
        refreshAttempts: 0,
    };

    // 최대 재시도 횟수
    private static readonly MAX_REFRESH_ATTEMPTS = 3;

    // 재시도 간격 (밀리초)
    private static readonly REFRESH_RETRY_DELAY = 1000;

    // 토큰 갱신 간격 (밀리초) - 너무 빈번한 갱신 방지
    private static readonly MIN_REFRESH_INTERVAL = 5000; // 5초로 단축

    private constructor() { }

    static getInstance(): AuthStateManager {
        if (!AuthStateManager.instance) {
            AuthStateManager.instance = new AuthStateManager();
        }
        return AuthStateManager.instance;
    }

    /**
     * 현재 상태 반환
     */
    getState(): AuthState {
        return { ...this.state };
    }

    /**
     * 토큰 갱신 시작
     */
    startRefresh(): void {
        this.state.isRefreshing = true;
        this.state.lastRefreshTime = Date.now();
        this.state.refreshAttempts++;
    }

    /**
     * 토큰 갱신 완료
     */
    completeRefresh(): void {
        this.state.isRefreshing = false;
        this.state.refreshPromise = null;
        this.state.refreshAttempts = 0;
    }

    /**
     * 토큰 갱신 실패
     */
    failRefresh(): void {
        this.state.isRefreshing = false;
        this.state.refreshPromise = null;
    }

    /**
     * 갱신 중인지 확인
     */
    isRefreshing(): boolean {
        return this.state.isRefreshing;
    }

    /**
     * 갱신 Promise 설정
     */
    setRefreshPromise(promise: Promise<string>): void {
        this.state.refreshPromise = promise;
    }

    /**
     * 갱신 Promise 가져오기
     */
    getRefreshPromise(): Promise<string> | null {
        return this.state.refreshPromise;
    }

    /**
     * 최대 재시도 횟수 초과 여부 확인
     */
    hasExceededMaxAttempts(): boolean {
        return this.state.refreshAttempts >= AuthStateManager.MAX_REFRESH_ATTEMPTS;
    }

    /**
     * 최소 갱신 간격 경과 여부 확인
     */
    canRefresh(): boolean {
        const timeSinceLastRefresh = Date.now() - this.state.lastRefreshTime;
        return timeSinceLastRefresh >= AuthStateManager.MIN_REFRESH_INTERVAL;
    }

    /**
     * 재시도 지연 시간 계산 (지수 백오프)
     */
    getRetryDelay(): number {
        return AuthStateManager.REFRESH_RETRY_DELAY * Math.pow(2, this.state.refreshAttempts - 1);
    }

    /**
     * 상태 초기화
     */
    reset(): void {
        this.state = {
            isRefreshing: false,
            refreshPromise: null,
            lastRefreshTime: 0,
            refreshAttempts: 0,
        };
    }

    /**
     * 유효한 토큰 가져오기 (갱신 포함)
     */
    async getValidToken(): Promise<string> {
        console.log('AuthStateManager.getValidToken 시작');

        const currentToken = TokenManager.getAccessToken();
        const refreshToken = TokenManager.getRefreshToken();

        console.log('토큰 상태 확인:', {
            hasAccessToken: !!currentToken,
            hasRefreshToken: !!refreshToken,
            accessTokenPreview: currentToken ? currentToken.substring(0, 20) + '...' : null,
            refreshTokenPreview: refreshToken ? refreshToken.substring(0, 20) + '...' : null
        });

        // Access token이 유효하고 갱신이 필요하지 않은 경우
        if (currentToken && !JWTValidator.shouldRefreshAccessToken(currentToken, refreshToken)) {
            const tokenInfo = TokenManager.getTokenInfo(currentToken);
            console.log('현재 Access token이 유효합니다:', {
                expiresIn: tokenInfo.expiresIn,
                isExpiringSoon: tokenInfo.isExpiringSoon,
                refreshTokenValid: refreshToken ? !JWTValidator.isTokenExpired(refreshToken) : false,
                tokenSubject: tokenInfo.subject
            });
            return currentToken;
        }

        console.log('Access token 갱신이 필요합니다:', {
            hasAccessToken: !!currentToken,
            hasRefreshToken: !!refreshToken,
            accessTokenInfo: currentToken ? TokenManager.getTokenInfo(currentToken) : null,
            refreshTokenInfo: refreshToken ? TokenManager.getTokenInfo(refreshToken) : null,
            shouldRefresh: JWTValidator.shouldRefreshAccessToken(currentToken || '', refreshToken)
        });

        // 이미 갱신 중인 경우 기존 Promise 반환
        if (this.isRefreshing() && this.getRefreshPromise()) {
            return this.getRefreshPromise()!;
        }

        // Refresh token이 없거나 만료된 경우
        if (!refreshToken || JWTValidator.isTokenExpired(refreshToken)) {
            throw new Error('Refresh token이 없거나 만료되었습니다. 다시 로그인해주세요.');
        }

        // 최대 재시도 횟수 초과
        if (this.hasExceededMaxAttempts()) {
            throw new Error('토큰 갱신 최대 재시도 횟수 초과');
        }

        // 최소 갱신 간격 미경과
        if (!this.canRefresh()) {
            const timeSinceLastRefresh = Date.now() - this.state.lastRefreshTime;
            const remainingTime = AuthStateManager.MIN_REFRESH_INTERVAL - timeSinceLastRefresh;
            console.warn(`토큰 갱신 간격이 너무 짧습니다. ${remainingTime}ms 후 재시도 가능`);
            throw new Error(`토큰 갱신 간격이 너무 짧습니다. ${Math.ceil(remainingTime / 1000)}초 후 재시도 가능`);
        }

        // 토큰 갱신 시작
        console.log('토큰 갱신 프로세스 시작');
        this.startRefresh();

        try {
            const refreshPromise = this.refreshToken();
            this.setRefreshPromise(refreshPromise);

            console.log('토큰 갱신 Promise 대기 중...');
            const newToken = await refreshPromise;

            console.log('토큰 갱신 성공:', {
                newTokenPreview: newToken.substring(0, 20) + '...',
                newTokenInfo: TokenManager.getTokenInfo(newToken)
            });

            this.completeRefresh();
            return newToken;
        } catch (error) {
            console.error('토큰 갱신 실패:', {
                error: error instanceof Error ? error.message : error,
                refreshAttempts: this.state.refreshAttempts,
                hasExceededMaxAttempts: this.hasExceededMaxAttempts()
            });
            this.failRefresh();
            throw error;
        }
    }

    /**
     * 토큰 갱신 실행 (재시도 로직 포함)
     */
    private async refreshToken(): Promise<string> {
        console.log('AuthStateManager.refreshToken 시작');

        const refreshToken = TokenManager.getRefreshToken();

        if (!refreshToken) {
            console.error('Refresh token이 없습니다');
            throw new Error('Refresh token이 없습니다');
        }

        // Refresh token 유효성 재확인
        if (JWTValidator.isTokenExpired(refreshToken)) {
            console.error('Refresh token이 만료되었습니다:', {
                refreshTokenPreview: refreshToken.substring(0, 20) + '...',
                refreshTokenInfo: TokenManager.getTokenInfo(refreshToken)
            });
            throw new Error('Refresh token이 만료되었습니다. 다시 로그인해주세요.');
        }

        console.log('토큰 갱신을 시작합니다:', {
            refreshTokenValid: !JWTValidator.isTokenExpired(refreshToken),
            refreshTokenExpiresIn: JWTValidator.getTokenExpirationTime(refreshToken),
            refreshTokenInfo: TokenManager.getTokenInfo(refreshToken)
        });

        // 재시도 로직을 사용한 토큰 갱신
        const retryResult = await RetryManager.retryTokenRefresh(async () => {
            // AuthService를 동적으로 import하여 순환 참조 방지
            const { AuthService } = await import('../services/AuthService');

            const response = await AuthService.refreshToken();

            if (response.success && response.data?.access_token) {
                // 새 토큰 저장
                TokenManager.setAccessToken(response.data.access_token);

                if (response.data.refresh_token) {
                    TokenManager.setRefreshToken(response.data.refresh_token);
                }

                console.log('토큰 갱신 성공:', {
                    newAccessTokenExpiresIn: JWTValidator.getTokenExpirationTime(response.data.access_token),
                    newRefreshTokenExpiresIn: response.data.refresh_token ? JWTValidator.getTokenExpirationTime(response.data.refresh_token) : 'N/A'
                });

                return response.data.access_token;
            } else {
                throw new Error(response.message || '토큰 갱신에 실패했습니다');
            }
        });

        // 재시도 모니터에 기록
        retryMonitor.recordAttempt(retryResult);

        if (retryResult.success) {
            return retryResult.result!;
        } else {
            console.error('토큰 갱신 실패:', retryResult.error);
            throw retryResult.error;
        }
    }
}

// 싱글톤 인스턴스 내보내기
export const authStateManager = AuthStateManager.getInstance();
export default authStateManager;
